import { Injectable } from '@angular/core';

import {BehaviorSubject, Observable, of, ReplaySubject} from 'rxjs';
import {tap, delay, map, distinctUntilChanged} from 'rxjs/operators';

import { ApiService } from './api.service';
import {JwtService} from './jwt.service';
import { UserModel } from '../models';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<UserModel>({} as UserModel);
  public currentUser = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();

  public isRememberMeChecked: boolean;

  isLoggedIn = false;

  // store the URL so we can redirect after logging in
  redirectUrl: string;

  endpoint = '/v2/api/employer/auth';
  constructor(private apiService: ApiService,
              private jwtService: JwtService,
              private http: HttpClient) {}

  // Verify JWT in localstorage with server & load user's info.
  // This runs once on application startup.
  populate() {
    // If JWT detected, attempt to get & store user's info
    if (this.jwtService.getToken()) {
      this.apiService.get('/v2/api/employer/user')
        .subscribe(
          data => {
            const user = data.user;
            user.company_logo = user.image;
            delete user.image;
            this.setAuth(user);
          },
          err => {
            this.logout();
          }
        );
    } else {
      // Remove any potential remnants of previous auth states
      this.logout();
    }
  }

  login(credentials): Observable<boolean> {
    return this.apiService.post(this.endpoint  + '/login', {user: credentials})
      .pipe(map(data => {
            if (data.success) {
              this.setAuth(data.user);
              this.isLoggedIn = true;
              return data;
            }
          }
      ));
  }

  register(credentials) {
    return this.http.post(environment.api_url + this.endpoint + '/register', {user: credentials});
  }

  saveLead(credentials): Observable<any> {
    return this.http.post(environment.api_url + this.endpoint + '/pre-register', {user: credentials});
  }

  setAuth(user: UserModel): void {
    // Save JWT sent from server in localstorage
    if (!this.jwtService.getToken()) {
      this.jwtService.saveToken(user.token);
    }
    // Set current user data into observable
    this.currentUserSubject.next(user);
    // Set isAuthenticated to true
    this.isAuthenticatedSubject.next(true);
  }


  logout(): void {
    this.isLoggedIn = false;
    // Remove JWT from localstorage
    this.jwtService.destroyToken();
    // Set current user to an empty object
    this.currentUserSubject.next({} as UserModel);
    // Set auth status to false
    this.isAuthenticatedSubject.next(false);
  }

  verification(url: string) {
    return this.apiService.post(  '/users/email-verification/' + url);
  }

  askResetPassword(mail: string) {
    return this.apiService.post(  '/users/forget', {email: mail});
  }

  checkResetPassword(token: string) {
    return this.apiService.get(  '/users/reset/' + token);
  }

  resetPassword(data: any) {
    return this.apiService.post(  '/users/reset/' + data.token, {password: data.password});
  }

  getCurrentUser(): UserModel {
    return this.currentUserSubject.value;
  }

  validateToken(token: string): Observable<boolean> {
    // Save the token temporarily to use for the validation request
    this.jwtService.saveToken('JWT ' + token);
    // Make a request to validate the token with the server
    return this.apiService.get('/v2/api/employer/user')
      .pipe(
        map(data => {
          if (data && data.user) {
            this.setAuth(data.user);
            return true;
          }
          return false;
        }),
        tap({
          error: () => {
            // If validation fails, remove the token
            this.jwtService.destroyToken();
          }
        })
      );
  }
}
